<template>
	<header class="hero-banner" :aria-label="t(`${I18N_NS}.hero.ariaLabel`)">
		<div class="hero-banner__scene" aria-hidden="true">
			<img class="hero-banner__bg" :src="bannerBgImage" :alt="t(`${I18N_NS}.meta.breadcrumb`)" />
		</div>

		<div class="hero-banner__inner pc-center-1200">
			<div class="hero-banner__carousel">
				<Transition name="hero-slide">
					<div
						v-if="activeSlide === 0"
						key="logo"
						class="hero-banner__slide hero-banner__slide--logo"
					>
						<img
							class="hero-banner__logo"
							:class="{ 'hero-banner__logo--en': isEn }"
							:src="heroLogoSrc"
							:alt="t(`${I18N_NS}.meta.breadcrumb`)"
						/>
					</div>

					<div v-else key="text" class="hero-banner__slide hero-banner__slide--text">
						<p class="hero-banner__slogan" :class="{ 'hero-banner__slogan--en': isEn }">
							{{ t(`${I18N_NS}.hero.slogan`) }}
						</p>
					</div>
				</Transition>
			</div>
			<div class="hero-banner__decor-wrap" aria-hidden="true">
				<img class="hero-banner__decor" :src="bannerDecorImage" alt="" />
			</div>
		</div>
	</header>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "~/composables/useI18n";
import bannerBgImage from "../../images/hero-banner-bg.png";
import bannerDecorImage from "../../images/hero-banner-decor.png";
import heroLogoTw from "~/assets/images/SeerQtw.svg";
import heroLogoEn from "~/assets/images/SeerQ.svg";

const I18N_NS = "aboutUs";
const { t, locale } = useI18n();
const isEn = computed(() => locale?.value === "en");
const heroLogoSrc = computed(() => (isEn.value ? heroLogoEn : heroLogoTw));

const activeSlide = ref(0);
const SLIDE_INTERVAL = 4000;
let slideTimer = null;

onMounted(() => {
	slideTimer = setInterval(() => {
		activeSlide.value = activeSlide.value === 0 ? 1 : 0;
	}, SLIDE_INTERVAL);
});

onUnmounted(() => {
	if (slideTimer) {
		clearInterval(slideTimer);
		slideTimer = null;
	}
});
</script>

<style lang="scss" scoped>
.hero-banner {
	position: relative;
	width: 100%;
	max-width: 100%;
	overflow: hidden;
	background: linear-gradient(180deg, #fffdf9 0%, #ffffff 100%);

	&__scene {
		position: relative;
		width: 100%;
		aspect-ratio: 3.15 / 1;
		min-height: 260px;
		max-height: 600px;
		overflow: hidden;
	}

	&__bg {
		position: absolute;
		inset: 0;
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}

	&__inner {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 1;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: clamp(24px, 4%, 64px);
		width: 100%;
		max-width: 1200px;
		min-width: 0;
		height: 100%;
		margin-left: auto;
		margin-right: auto;
		box-sizing: border-box;
		pointer-events: none;
	}

	&__decor-wrap {
		position: relative;
		flex: 0 1 auto;
		width: min(54%, 620px);
		height: min(86%, 460px);
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 0;
		pointer-events: none;
	}

	&__decor {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
		object-position: center center;
		pointer-events: none;
	}

	&__carousel {
		position: relative;
		z-index: 1;
		flex: 0 1 auto;
		width: min(42%, 460px);
		min-width: 0;
		height: min(48%, 280px);
		min-height: 120px;
		overflow: hidden;
		pointer-events: auto;
	}

	&__slide {
		position: absolute;
		top: 0;
		left: 0;
		display: flex;
		flex-direction: column;
		justify-content: center;
		width: 100%;
		height: 100%;

		&--logo,
		&--text {
			align-items: flex-start;
		}
	}

	&__logo {
		display: block;
		width: 100%;
		max-width: 100%;
		height: auto;

		&--en {
			max-width: min(360px, 100%);
		}
	}

	&__slogan {
		margin: 0;
		font-family: "Source Han Serif CN", serif;
		font-weight: 700;
		color: #333333;
		line-height: 1.45;
		font-size: clamp(24px, 3.2vw, 52px);
		word-break: normal;
		overflow-wrap: break-word;

		&--en {
			font-size: clamp(20px, 2.4vw, 36px);
			line-height: 1.35;
		}
	}
}

@media (max-width: 1200px) {
	.hero-banner {
		&__scene {
			aspect-ratio: 2.8 / 1;
			min-height: 240px;
			max-height: 520px;
		}

		&__inner {
			width: 100%;
			padding-left: 24px;
			padding-right: 24px;
		}

		&__decor-wrap {
			width: min(52%, 560px);
			height: min(84%, 400px);
		}

		&__carousel {
			width: min(44%, 400px);
			height: min(50%, 240px);
		}
	}
}

@media (max-width: 1024px) {
	.hero-banner {
		&__scene {
			aspect-ratio: 2.5 / 1;
			min-height: 220px;
			max-height: 460px;
		}

		&__decor-wrap {
			width: min(50%, 480px);
			height: min(82%, 360px);
		}

		&__carousel {
			width: min(46%, 360px);
			height: min(52%, 220px);
		}

		&__slogan {
			font-size: clamp(22px, 4vw, 36px);

			&--en {
				font-size: clamp(18px, 3.2vw, 28px);
			}
		}
	}
}

@media (max-width: 900px) {
	.hero-banner {
		&__scene {
			aspect-ratio: 2.2 / 1;
			min-height: 200px;
			max-height: 400px;
		}

		&__decor-wrap {
			width: min(48%, 420px);
			height: min(80%, 320px);
		}

		&__carousel {
			width: min(48%, 320px);
			height: min(54%, 200px);
		}
	}
}

@media (max-width: 768px) {
	.hero-banner {
		&__scene {
			aspect-ratio: 1.9 / 1;
			min-height: 180px;
			max-height: 360px;
		}

		&__inner {
			width: 100%;
			padding-left: 20px;
			padding-right: 20px;
			gap: 16px;
		}

		&__decor-wrap {
			width: min(48%, 360px);
			height: min(78%, 280px);
		}

		&__carousel {
			width: min(50%, 280px);
			height: min(56%, 180px);
			min-height: 100px;
		}

		&__logo--en {
			max-width: min(280px, 100%);
		}

		&__slogan {
			font-size: clamp(20px, 5vw, 28px);

			&--en {
				font-size: clamp(16px, 4.2vw, 22px);
			}
		}
	}
}

@media (max-width: 600px) {
	.hero-banner {
		&__scene {
			aspect-ratio: 1.65 / 1;
			min-height: 160px;
			max-height: 300px;
		}

		&__decor-wrap {
			width: min(46%, 300px);
			height: min(76%, 240px);
		}

		&__carousel {
			width: min(52%, 240px);
			height: min(58%, 160px);
		}
	}
}

@media (max-width: 480px) {
	.hero-banner {
		&__scene {
			aspect-ratio: 1.5 / 1;
			min-height: 150px;
			max-height: 260px;
		}

		&__decor-wrap {
			width: min(44%, 260px);
			height: min(74%, 200px);
		}

		&__carousel {
			width: min(54%, 220px);
			height: min(60%, 140px);
		}

		&__slogan {
			font-size: 20px;
			line-height: 1.4;

			&--en {
				font-size: 16px;
			}
		}
	}
}

.hero-slide-enter-active,
.hero-slide-leave-active {
	transition: transform 0.55s ease;
}

.hero-slide-enter-from {
	transform: translateY(100%);
}

.hero-slide-leave-to {
	transform: translateY(-100%);
}
</style>
